import { useContext, useEffect, useRef, useState } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { UserContext } from '@components/providers/UserProvider/UserProvider';
import { BUYER_LINE_ITEMS_PAGINATION_DEFAULT } from '@enums/flags';
import LaunchDarklyService from '@services/LaunchDarklyService';
import type { EventRequest } from 'types';

// One race covers every way the read can hang: identify rejecting or never settling, or no LaunchDarkly client.
export const FLAG_READ_TIMEOUT_MS = 5_000;

export interface PaginationFlagRead {
  newSmartBaseline: boolean;
  pagination: boolean;
}

const FLAGS_OFF: PaginationFlagRead = { newSmartBaseline: false, pagination: false };

/**
 * The two raw flag values that pick an event's buyer Line Items load path, read from the LaunchDarkly client
 * after an `identify` this hook starts itself, so the anonymous visitor's values are never used. Undefined until
 * that read settles; after five seconds without it, both flags read off and the buyer gets today's grid.
 * Read once per event (`eventId`): the provider combines the values and stores the answer with the loaded data.
 */
export const usePaginationEnabled = (eventId?: EventRequest['id']): PaginationFlagRead | undefined => {
  const ldClient = useLDClient();
  const user = useContext(UserContext);
  const [read, setRead] = useState<{ eventId: EventRequest['id']; flags: PaginationFlagRead }>();
  // The race's deadline, set once per event so a client or user arriving later never restarts it.
  const deadline = useRef<{ at: number; eventId: EventRequest['id'] }>();
  const settled = read !== undefined && read.eventId === eventId;

  useEffect(() => {
    if (eventId === undefined || settled) return undefined;

    if (deadline.current?.eventId !== eventId) deadline.current = { at: Date.now() + FLAG_READ_TIMEOUT_MS, eventId };
    let cancelled = false;
    const settle = (flags: PaginationFlagRead) => {
      if (!cancelled) setRead({ eventId, flags });
    };
    const timeout = setTimeout(() => settle(FLAGS_OFF), Math.max(deadline.current.at - Date.now(), 0));

    LaunchDarklyService.configureUser(ldClient, user)
      ?.then(() => {
        clearTimeout(timeout);
        settle({
          newSmartBaseline: Boolean(ldClient?.variation('tlc-new-smart-baseline', false)),
          pagination: Boolean(ldClient?.variation('buyer-line-items-pagination', BUYER_LINE_ITEMS_PAGINATION_DEFAULT)),
        });
      })
      .catch(() => {
        clearTimeout(timeout);
        settle(FLAGS_OFF);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [eventId, ldClient, settled, user]);

  return settled ? read.flags : undefined;
};
